import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { Typography } from "../Typography";
import { Button } from "../Button";

// Define the animation keyframes
const blinkRowAnimation = keyframes`
  0%, 100% {
    background: transparent;
    box-shadow: none;
    opacity: 1;
  }
  50% {
    background: rgba(3, 255, 143, 0.5);
  }
`;

export type ColumnDefinition<T> = {
	header: string;
	accessor: keyof T | ((item: T) => React.ReactNode);
	render?: (value: any, item: T) => React.ReactNode;
};

export type TableProps<T extends { id: string | number }> = {
	data: T[];
	columns: ColumnDefinition<T>[];
	highlightOnUpdate?: boolean;
	paginate?: number;
	headerColor?: string;
	showHeaderSeparator?: boolean;
	tableContainerStyle?: React.CSSProperties;
	showLastRowSeparator?: boolean;
};

const TableContainer = styled.div`
	width: 100%;
	overflow-x: auto;
	background-color: var(--surface);
	border-radius: 0.5rem;
	padding: 1.25rem;
`;

const StyledTable = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const TableHeader = styled.thead<{ $showSeparator: boolean }>`
	border-bottom: ${({ $showSeparator }) =>
		$showSeparator ? "1px solid var(--outline-variant)" : "none"};
`;

const TableHeaderCell = styled.th`
	padding: 1.25rem 1.125rem;
	text-align: left;
	color: var(--text-subheading);
`;

const TableRow = styled.tr<{
	$highlight: boolean;
	$showLastRowSeparator: boolean;
}>`
	border-bottom: 1px solid var(--outline-variant);

	${({ $highlight }) =>
		$highlight &&
		css`
			animation: ${blinkRowAnimation} 1.5s ease;
		`}

	&:last-child {
		border-bottom: ${({ $showLastRowSeparator }) =>
			$showLastRowSeparator === false && "none"};
	}
`;

const TableCell = styled.td`
	padding: 1.25rem 1.125rem;
	color: var(--text-paragraph);
`;

const PaginationContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	margin-top: 1rem;
`;

const PaginationButtonContainer = styled.div`
	display: flex;
	gap: 1rem;
	margin-left: auto;
`;

const ensureReactNode = (content: any): React.ReactNode => {
	if (React.isValidElement(content)) {
		return content;
	}
	if (
		typeof content === "string" ||
		typeof content === "number" ||
		typeof content === "boolean"
	) {
		return content.toString();
	}
	return JSON.stringify(content);
};

type PaginationProps = {
	currentPage: number;
	totalPages: number;
	itemsPerPage: number;
	totalItems: number;
	onPageChange: (page: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({
	currentPage,
	totalPages,
	itemsPerPage,
	totalItems,
	onPageChange,
}) => {
	const startItem = (currentPage - 1) * itemsPerPage + 1;
	const endItem = Math.min(currentPage * itemsPerPage, totalItems);

	return (
		<PaginationContainer>
			<Typography variant="p" color="var(--text-paragraph)">
				Showing {startItem} to {endItem} of {totalItems} results
			</Typography>
			<PaginationButtonContainer>
				<Button
					onClick={() => onPageChange(currentPage - 1)}
					disabled={currentPage === 1}
					label="Previous"
					variant="text"
				/>
				<Button
					onClick={() => onPageChange(currentPage + 1)}
					disabled={currentPage === totalPages}
					label="Next"
					variant="text"
				/>
			</PaginationButtonContainer>
		</PaginationContainer>
	);
};

export function Table<T extends { id: string | number }>({
	data,
	columns,
	highlightOnUpdate,
	paginate,
	headerColor,
	showHeaderSeparator = false,
	tableContainerStyle,
	showLastRowSeparator = true,
}: TableProps<T>) {
	const [newIds, setNewIds] = useState<Set<string | number>>(new Set());
	const prevDataIds = useRef<Set<string | number>>(new Set());
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		if (!highlightOnUpdate || !data?.length || !data[0]?.id) {
			return;
		}

		const currentIds = new Set(data.map((d) => d.id));
		const newItemIds = new Set(
			Array.from(currentIds).filter((id) => !prevDataIds.current.has(id))
		);
		setNewIds(newItemIds);
		prevDataIds.current = currentIds;
	}, [data, highlightOnUpdate]);

	const itemsPerPage = paginate || data.length;
	const totalPages = Math.ceil(data.length / itemsPerPage);
	const paginatedData = paginate
		? data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
		: data;

	return (
		<TableContainer style={tableContainerStyle}>
			<StyledTable>
				<TableHeader $showSeparator={showHeaderSeparator}>
					<tr>
						{columns.map((column, index) => (
							<TableHeaderCell key={index}>
								<Typography
									variant="p"
									color={headerColor || "var(--text-heading)"}
									fontWeight={500}
								>
									{column.header}
								</Typography>
							</TableHeaderCell>
						))}
					</tr>
				</TableHeader>
				<tbody>
					{paginatedData.map((item, idx) => (
						<TableRow
							key={idx}
							$highlight={newIds.has(item.id)}
							$showLastRowSeparator={showLastRowSeparator}
						>
							{columns.map((column, cellIndex) => (
								<TableCell key={cellIndex}>
									{column.render ? (
										column.render(
											typeof column.accessor === "function"
												? column.accessor(item)
												: item[column.accessor],
											item
										)
									) : (
										<Typography variant="p" color="var(--text-paragraph)">
											{ensureReactNode(
												typeof column.accessor === "function"
													? column.accessor(item)
													: item[column.accessor]
											)}
										</Typography>
									)}
								</TableCell>
							))}
						</TableRow>
					))}
				</tbody>
			</StyledTable>
			{paginate && (
				<Pagination
					currentPage={currentPage}
					totalPages={totalPages}
					itemsPerPage={itemsPerPage}
					totalItems={data.length}
					onPageChange={setCurrentPage}
				/>
			)}
		</TableContainer>
	);
}
